import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCase, getCasePhases, deleteCase } from '../../services/caseService';
import { getPatient } from '../../services/patientService';
import { getPhaseConfig, getPhaseCompletionStatus } from '../../constants/phasePhotoConfigs';
import { getPhasePhotos } from '../../services/photoService';

const CaseDetail = () => {
  const { patientId, caseId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [treatmentCase, setTreatmentCase] = useState(null);
  const [patient, setPatient] = useState(null);
  const [phases, setPhases] = useState([]);
  const [phasePhotoCounts, setPhasePhotoCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [patientId, caseId, userProfile]);

  const loadData = async () => {
    if (!userProfile) return;

    try {
      setLoading(true);
      setError('');

      const [caseData, patientData, phasesData] = await Promise.all([
        getCase(userProfile.practiceId, userProfile.id, patientId, caseId),
        getPatient(userProfile.practiceId, userProfile.id, patientId),
        getCasePhases(userProfile.practiceId, patientId, caseId)
      ]);

      setTreatmentCase(caseData);
      setPatient(patientData);
      setPhases(phasesData);

      // Load photo counts for each phase
      const counts = {};
      await Promise.all(
        phasesData.map(async (phase) => {
          try {
            const photos = await getPhasePhotos(
              userProfile.practiceId, patientId, caseId, phase.id
            );
            counts[phase.id] = photos.map(p => p.category);
          } catch (e) {
            counts[phase.id] = [];
          }
        })
      );
      setPhasePhotoCounts(counts);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteCase(userProfile.practiceId, userProfile.id, patientId, caseId, treatmentCase.name);
      navigate(`/patients/${patientId}`);
    } catch (err) {
      console.error('Error deleting case:', err);
      setError('Failed to delete case');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getPhaseColor = (phaseType) => {
    const colors = {
      initial: 'blue',
      postOrtho: 'green',
      provisional: 'amber',
      final: 'purple'
    };
    return colors[phaseType] || 'gray';
  };

  const getPhaseStatus = (status) => {
    const badges = {
      not_started: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800'
    };
    return badges[status] || badges.not_started;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !treatmentCase) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error || 'Case not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/patients/${patientId}`}
          className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
        >
          &larr; Back to patient
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{treatmentCase.name}</h1>
            {patient && (
              <p className="mt-1 text-sm text-gray-600">
                Patient: {patient.firstName} {patient.lastName}
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/patients/${patientId}/cases/${caseId}/compare`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              Compare Photos
            </Link>
            <Link
              to={`/patients/${patientId}/cases/${caseId}/smile-design`}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              AI Smile Design
            </Link>
            <Link
              to={`/patients/${patientId}/cases/${caseId}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Edit
            </Link>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Case Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Case Information</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                treatmentCase.status === 'active' ? 'bg-green-100 text-green-800' :
                treatmentCase.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {treatmentCase.status || 'active'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Version</dt>
            <dd className="mt-1 text-sm text-gray-900">v{treatmentCase.currentVersion || 1}</dd>
          </div>
          {treatmentCase.description && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900">{treatmentCase.description}</dd>
            </div>
          )}
          {treatmentCase.treatmentGoals && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Treatment Goals</dt>
              <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{treatmentCase.treatmentGoals}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Treatment Phases */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Treatment Phases</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage photos and track progress for each treatment phase
          </p>
        </div>
        <div className="divide-y divide-gray-200">
          {phases.map((phase) => {
            const phaseConfig = getPhaseConfig(phase.phaseType);
            const uploadedCats = phasePhotoCounts[phase.id] || [];
            const completion = getPhaseCompletionStatus(phase.phaseType, uploadedCats);
            const color = getPhaseColor(phase.phaseType);

            return (
              <div key={phase.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full bg-${color}-500`}></div>
                      <h3 className="text-base font-medium text-gray-900">
                        {phaseConfig?.name || phase.phaseType}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPhaseStatus(phase.status)}`}>
                        {phase.status?.replace('_', ' ') || 'not started'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 ml-6">
                      {phaseConfig?.description}
                    </p>
                    {phase.notes && (
                      <p className="mt-2 text-sm text-gray-600 ml-6">{phase.notes}</p>
                    )}

                    {/* Progress bar */}
                    <div className="mt-3 ml-6">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>{completion.uploaded} / {completion.total} photos</span>
                        <span>{completion.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 max-w-md">
                        <div
                          className={`bg-${color}-500 h-1.5 rounded-full transition-all`}
                          style={{ width: `${completion.progress}%` }}
                        ></div>
                      </div>
                      {completion.missingRequired.length > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {completion.missingRequired.length} required photo(s) missing
                        </p>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/patients/${patientId}/cases/${caseId}/phases/${phase.id}/photos`}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Manage Photos
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Tools */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to={`/patients/${patientId}/cases/${caseId}/exam`}
          className="bg-white shadow rounded-lg p-5 hover:shadow-md transition group"
        >
          <div className="flex items-center mb-2">
            <div className="bg-amber-100 rounded-md p-2 mr-3 group-hover:bg-amber-200 transition">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Clinical Exam</h3>
          </div>
          <p className="text-xs text-gray-500">
            Dawson Complete Dental Exam — 17 diagnostic sections
          </p>
        </Link>

        <Link
          to={`/patients/${patientId}/cases/${caseId}/compare`}
          className="bg-white shadow rounded-lg p-5 hover:shadow-md transition group"
        >
          <div className="flex items-center mb-2">
            <div className="bg-blue-100 rounded-md p-2 mr-3 group-hover:bg-blue-200 transition">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Before & After</h3>
          </div>
          <p className="text-xs text-gray-500">
            Compare photos across phases
          </p>
        </Link>

        <Link
          to={`/patients/${patientId}/cases/${caseId}/smile-design`}
          className="bg-white shadow rounded-lg p-5 hover:shadow-md transition group"
        >
          <div className="flex items-center mb-2">
            <div className="bg-indigo-100 rounded-md p-2 mr-3 group-hover:bg-indigo-200 transition">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">AI Smile Design</h3>
          </div>
          <p className="text-xs text-gray-500">
            Golden proportion smile mock-ups
          </p>
        </Link>

        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center mb-2">
            <div className="bg-green-100 rounded-md p-2 mr-3">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">HIPAA Compliant</h3>
          </div>
          <p className="text-xs text-gray-500">
            Encrypted photos + audit logging
          </p>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-gray-50 shadow rounded-lg px-6 py-4">
        <dl className="grid grid-cols-2 gap-4 text-xs text-gray-500">
          <div>
            <dt className="font-medium">Created</dt>
            <dd className="mt-1">{formatDate(treatmentCase.createdAt)}</dd>
          </div>
          <div>
            <dt className="font-medium">Last Updated</dt>
            <dd className="mt-1">{formatDate(treatmentCase.updatedAt)}</dd>
          </div>
        </dl>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => !deleting && setShowDeleteModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium text-gray-900">Delete Case</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this case? This will permanently delete all photos and phase data.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDetail;
